import React, { useEffect, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle, ArrowRight, Globe2 } from "lucide-react";
import { waLink, quoteMessage } from "@/lib/whatsapp";

// Public TopoJSON of world countries (110m).
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

export default function WorldMap({ branches = [], onSelect, globalPresenceMap = false, homePreview = false, onMobileCardClick, interactive = true }) {
  const [hovered, setHovered] = useState(null);
  const [mapScale, setMapScale] = useState(175);
  const [compactViewport, setCompactViewport] = useState(false);
  const countryCodes = new Set(branches.map(b => b.country_code));

  useEffect(() => {
    const compactViewport = window.matchMedia("(max-width: 1023px)");
    const updateScale = () => {
      setCompactViewport(compactViewport.matches);
      setMapScale(compactViewport.matches ? 205 : 175);
    };
    updateScale();
    compactViewport.addEventListener("change", updateScale);
    return () => compactViewport.removeEventListener("change", updateScale);
  }, []);

  const openHomeGlobalPage = (event) => {
    if (homePreview && compactViewport && onMobileCardClick) {
      event.preventDefault();
      onMobileCardClick();
    }
  };

  return (
    <div data-testid={homePreview ? "home-world-map-card" : "world-map-wrapper"} onClick={interactive ? openHomeGlobalPage : undefined} onTouchEnd={interactive ? openHomeGlobalPage : undefined} className={`relative bg-gradient-to-br from-[#0B0F12] via-[#0F382C] to-[#0B0F12] text-white rounded-sm overflow-hidden border border-emerald-500/20 shadow-2xl ${globalPresenceMap ? "global-presence-map" : ""} ${homePreview ? "home-world-map-preview" : ""}`}>
      {/* Header overlay */}
      <div className="world-map-header absolute top-3 left-3 md:top-6 md:left-6 z-10 max-w-[80%] md:max-w-xs pointer-events-none">
        <div className="flex items-center gap-2 mb-1 md:mb-2">
          <Globe2 size={12} className="text-emerald-300 md:hidden"/>
          <Globe2 size={14} className="text-emerald-300 hidden md:block"/>
          <div className="overline text-emerald-300 text-[9px] md:text-[10px]">{globalPresenceMap ? "Global Presence" : "PIPECO Global Presence"}</div>
        </div>
        <div className="world-map-branch-count font-display text-base sm:text-xl md:text-2xl lg:text-3xl font-black leading-tight">26 Branches · 22 Countries</div>
        <div className="text-[10px] md:text-xs text-white/60 mt-1 hidden md:block">Since 2010 · Tap or hover a pin to see office details</div>
      </div>
      {/* Legend / stats overlay */}
      <div className="world-map-status absolute top-3 right-3 md:top-6 md:right-6 z-10 flex items-center gap-1.5 md:gap-2 pointer-events-none">
        <span className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-emerald-400 shadow-[0_0_10px_#25D366]"/>
        <span className="text-[9px] md:text-[10px] uppercase tracking-widest text-white/70">Active Office</span>
      </div>
      <ComposableMap
        data-testid="world-map"
        projectionConfig={{ scale: mapScale, rotate: [-11, 0, 0] }}
        width={980}
        height={520}
        className="world-map-canvas"
        style={{ width: "100%", height: "auto", background: "transparent", touchAction: "manipulation" }}
      >
        <ZoomableGroup center={[20, 15]} zoom={1} minZoom={1} maxZoom={interactive ? 4 : 1} filterZoomEvent={(evt) => {
          // Allow taps on markers to pass through — only zoom on pinch/wheel
          return interactive && evt.type !== "mousedown" && evt.type !== "touchstart";
        }}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const iso2 = ISO_NUMERIC_TO_ALPHA2[geo.id] || "";
                const active = countryCodes.has(iso2);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    className={globalPresenceMap ? `global-map-geography ${active ? "global-map-geography-active" : ""}` : undefined}
                    style={{
                      default: {
                        fill: active ? "#1A5644" : "#12181C",
                        stroke: "#0B0F12",
                        strokeWidth: 0.4,
                        outline: "none",
                      },
                      hover: { fill: active ? "#25D366" : "#1A5644", stroke: "#0B0F12", strokeWidth: 0.5, outline: "none" },
                      pressed: { fill: "#25D366", outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>
          {branches.map((b, index) => (
            <Marker key={b.id} coordinates={[b.lng, b.lat]}>
              <g
                className="map-marker"
                onClick={interactive ? () => onSelect && onSelect(b) : undefined}
                onTouchEnd={interactive ? (e) => { e.preventDefault(); onSelect && onSelect(b); } : undefined}
                onMouseEnter={interactive ? () => setHovered(b) : undefined}
                onMouseLeave={interactive ? () => setHovered(null) : undefined}
                data-testid={`map-pin-${b.country_code}`}
                style={{ cursor: interactive ? "pointer" : "default", touchAction: "manipulation" }}
              >
                {/* Invisible ≥44px touch target (per Apple/Google guidelines) */}
                <circle r={18} fill="transparent" className="map-pin-hit-area" pointerEvents="all"/>
                <circle r={11} fill="#25D366" opacity={0.22} className="pulse-dot map-pin-pulse" pointerEvents="none"/>
                <circle r={6} fill="#25D366" stroke="#fff" strokeWidth={1.6} className="map-pin-core" pointerEvents="none"/>
                <circle r={2} fill="#0B0F12" pointerEvents="none"/>
              </g>
              {homePreview && branches.findIndex(branch => (branch.country_code || branch.country) === (b.country_code || b.country)) === index && <text className="home-map-country-label" x={b.lng > 100 ? -10 : 10} y={index % 2 ? 15 : -10} textAnchor={b.lng > 100 ? "end" : "start"} pointerEvents="none">{b.country}</text>}
            </Marker>
          ))}
        </ZoomableGroup>
      </ComposableMap>
      {interactive && hovered && (
        <div className="absolute bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-auto md:max-w-md bg-black/85 backdrop-blur-md border border-emerald-300/30 p-4 md:p-5">
          <div className="overline text-emerald-300 text-[10px]">{hovered.branch_type}</div>
          <div className="font-display text-lg md:text-2xl mt-1 leading-tight">{hovered.name}</div>
          <div className="text-white/70 text-xs md:text-sm">{hovered.city}, {hovered.country}</div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 mt-2 md:mt-3">Tap pin for full details →</div>
        </div>
      )}
    </div>
  );
}

// Minimal ISO numeric → ISO alpha-2 mapping for countries where PIPECO has an office
// (Used to highlight those countries on the map).
const ISO_NUMERIC_TO_ALPHA2 = {
  "784":"AE","512":"OM","634":"QA","400":"JO","682":"SA","048":"BH","818":"EG",
  "710":"ZA","716":"ZW","231":"ET","288":"GH","356":"IN","144":"LK","458":"MY",
  "360":"ID","704":"VN","826":"GB",
};

export function BranchDetailCard({ branch, onClose }) {
  if (!branch) return null;
  return (
    <div data-testid="branch-detail-card" className="fixed inset-0 z-[55] grid place-items-end sm:place-items-center bg-black/70 backdrop-blur-sm p-0 sm:p-6 fade-in-up" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg shadow-2xl" onClick={(e)=>e.stopPropagation()}>
        <div className="aspect-16-9 overflow-hidden bg-gradient-to-br from-pipeco-green to-pipeco-dark relative">
          {branch.main_image
            ? <img src={branch.main_image} alt={branch.name} className="w-full h-full object-cover"/>
            : <div className="w-full h-full grid place-items-center text-white/60"><div className="text-center"><Globe2 size={40} className="mx-auto text-emerald-300 mb-2"/><div className="font-mono text-[10px] tracking-widest uppercase">Office photo coming soon</div></div></div>}
          <button data-testid="branch-close" onClick={onClose} className="absolute top-3 right-3 bg-black/50 text-white w-9 h-9 grid place-items-center hover:bg-black/80">✕</button>
        </div>
        <div className="p-6">
          <div className="overline text-pipeco-green">{branch.branch_type}</div>
          <h3 className="headline text-2xl mt-1 text-slate-900">{branch.name}</h3>
          <div className="text-slate-600 text-sm mt-1">{branch.city}, {branch.country}</div>
          {branch.address && <div className="mt-3 text-sm text-slate-700">{branch.address}</div>}
          {branch.working_hours && <div className="mt-2 text-xs uppercase tracking-widest text-slate-500">Hours: {branch.working_hours}</div>}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-5">
            <a data-testid="branch-call" href={`tel:${branch.phone || branch.whatsapp}`} className="btn-outline-dark justify-center text-[10px] py-2.5"><Phone size={14}/>Call</a>
            <a data-testid="branch-whatsapp" href={waLink(branch.whatsapp || branch.phone, quoteMessage())} target="_blank" rel="noreferrer" className="btn-outline-dark justify-center text-[10px] py-2.5 !border-[color:var(--wa-green)] !text-[color:var(--wa-green)] hover:!bg-[color:var(--wa-green)] hover:!text-white"><MessageCircle size={14}/>WhatsApp</a>
            <a data-testid="branch-email" href={`mailto:${branch.email}`} onClick={(e)=>e.stopPropagation()} className="btn-outline-dark justify-center text-[10px] py-2.5"><Mail size={14}/>Email</a>
            <a data-testid="branch-directions" href={branch.maps_link || `https://www.google.com/maps/search/${branch.lat},${branch.lng}`} target="_blank" rel="noreferrer" className="btn-outline-dark justify-center text-[10px] py-2.5"><MapPin size={14}/>Directions</a>
          </div>
          <Link data-testid="branch-view-office" to={`/contact?country=${encodeURIComponent(branch.country)}`} onClick={onClose} className="btn-primary w-full justify-center mt-3">
            View Office <ArrowRight size={14}/>
          </Link>
        </div>
      </div>
    </div>
  );
}
